import * as React from 'react';

import Alert from 'antd/es/alert';
import Modal from 'antd/es/modal';

import loadable from '@common/react/loadable/loadableSettings';
import { ItemProvider } from '@common/react/components/Core/ItemProvider/ItemProvider';
import Loader from '@common/react/components/Core/LoaderProvider/Loader';
import Button from '@common/react/components/Forms/Button';
import ItemsProviderWithStore from '@common/react/components/Core/ItemsProviderWithStore/ItemsProviderWithStore';
import { setLocalStorageValue } from '@common/react/utils/localStorage/localStorage';

import QuestionnaireForm from '@commonTuna/react/components/UI/QuestionnaireForm/QuestionnaireForm';
import { BaseTestSession } from '@commonTuna/react/objects/BaseTestSession';
import { BaseInvite, InviteStatus } from '@commonTuna/react/objects/BaseInvite';

const ConsentFormSwitcher = loadable(() =>
	import(/* webpackChunkName: "ConsentFormSwitcher" */ '@commonTuna/react/components/UI/ConsentForm/ConsentFormSwitcher'));

interface ComponentProps {
	afterSubmit?: (answeredInviteIds: Array<number>) => void;
	renderButtons?: (handleSubmit: () => void) => React.ReactNode;
	invites?: Array<BaseInvite>;
	onSkip?: () => void;
}

const InitHandler: React.FC<{ onInit }> = ({ onInit }) => {
	React.useEffect(() => {
		onInit();
	}, []);
	return <></>;
};

export const RegistrationFillForms: React.FC<ComponentProps> = (props) => {
	const [hasSigh, setHasSign] = React.useState<boolean>(false);
	const [activeInviteIndex, setActiveInviteIndex] = React.useState<null | number>(null);
	const [open, setOpen] = React.useState(true);
	const [modalOpen, setModalOpen] = React.useState(false);
	const [answeredInvites, setAnsweredInvites] = React.useState<Array<number>>([]);

	React.useEffect(() => {
		setModalOpen(!!props.invites);
	}, []);

	const onInit = (inviteList) => {
		if (inviteList?.length > 0) {
			let index = 0;
			while ((index < inviteList.length) && inviteList[index].status === InviteStatus.Done) {
				index++;
			}
			if (index < inviteList.length) {
				setActiveInviteIndex(index);
			} else {
				Modal.success({
					title: 'All forms are already filled out',
					onOk: handleSubmit,
				});
			}
		} else {
			handleSubmit();
		}
	};

	const handleSubmit = (ids: Array<number> = [], setItems?) => {
		setLocalStorageValue('skipFillInvites', true);
		setItems && clearInvites(ids, setItems);
		props.afterSubmit && props.afterSubmit(ids);
	};

	const nextInvite = (invites, setItems, invite?) => {
		let nextIndex = ((activeInviteIndex !== null) && (activeInviteIndex >= 0)) ? activeInviteIndex + 1 : -1;
		while ((nextIndex > 0) && (nextIndex < invites.length) && invites[nextIndex].status === InviteStatus.Done) {
			nextIndex++;
		}
		if ((nextIndex > 0) && (nextIndex < invites.length)) {
			setActiveInviteIndex(nextIndex);
		} else {
			handleSubmit(answeredInvites.concat(invite ? [invite] : []), setItems);
		}
	};

	const clearInvites = (items, setItems) => {
		setItems((prev) => prev.filter((item) => !items.includes(item)));
	};

	const goToDashboard = (setItems) => <button
		type="button"
		className="btn btn-primary pull-right"
		onClick={() => handleSubmit(answeredInvites, setItems)}
	>
		Go to dashboard
	</button>;

	if (props.invites && open) {
		return <Modal
			open={modalOpen}
			title="Do you want to fill forms?"
			onCancel={() => {
				props.onSkip?.();
			}}
			width="min(80vw, 400px)"
			closable={false}
			footer={<div className="text-end">
				<Button
					className="btn btn-danger"
					onClick={() => {
						props.onSkip?.();
						setLocalStorageValue('skipFillInvites', true);
					}}
				>
					No
				</Button>
				<Button
					className="btn btn-primary"
					other={{
						style: { height: 34 },
					}}
					onClick={() => {
						setOpen(false);
					}}
				>
					Yes
				</Button>
			</div>}
		/>;
	}

	return <ItemsProviderWithStore<BaseInvite>
		storeName="companyTemplateInvites"
		loadRequest="inviteList"
		pagination={{ current: 1, pageSize: 100 }}
		filters={{
			isCompanyTemplate: true,
			isAnswered: false,
			onlyFirstCopy: true,
		}}
	>
		{({ state: { items }, actions: { setItems } }) => {
			const invites = props.invites || items.filter((item) => !item.isCompanyTemplate) || [];
			const setNextInvite = (invite?) => {
				nextInvite(invites, setItems, invite);
			};
			return <>
				<div className="contact-text mt10 mb10">
					<Alert
						message={<>
							Sign the
							{' '}
							<b>forms</b>
							{' '}
							below and answer the
							{' '}
							<b>questions</b>
							.
						</>}
						type="info"
					/>
				</div>
				<div className="patient-register-page__photo" id="fill-forms">
					<InitHandler onInit={() => onInit(invites)} />
					{(invites?.length > 0) && (activeInviteIndex !== null) && (activeInviteIndex >= 0)
						? <div className="panel panel-default" style={{ width: '100%' }}>
							<div className="panel-body">
								{invites.map((invite, index) => {
									if (index === activeInviteIndex) {
										const queryParams = {
											appointmentProcedureId: 0,
											inviteId: invite.id,
											templateSnapshotId: invite.templateSnapshotId,
											appointmentId: invite.appointmentId ?? 0,
											diseaseId: invite.diseaseId,
											patientId: invite.patientId,
										};
										if (invite.diseaseId > 0) {
											return (
												<ItemProvider<BaseTestSession & {copyForwardDisabled?}>
													key={invite.diseaseId}
													id={1}
													saveRequest="testSession"
													loadRequest="getTestSession"
													additionalParams={{
														inviteId: invite.id,
														templateSnapshotId: invite.templateSnapshotId,
														appointmentId: invite.appointmentId ?? 0,
														diseaseId: invite.diseaseId,
														patientId: invite.patientId,
														withDisease: true,
													}}
												>
													{({ state: { itemLoading, item: session, error }, actions }) => {
														const signatureSrc = session?.files?.list[0]?.file?.src;
														const setSignatureSrc = (signatureSrc) => actions.setItem({
															...session,
															files: {
																list: [{ file: { src: signatureSrc } }],
															} as any,
														});
														const copyForwardDisabled = (session as any)?.copyForwardDisabled
															?? (!session?.oldTestSessionId || (session?.oldTestSessionId <= 0));
														const setCopyForwardDisabled = (copyForwardDisabled) => {
															actions.setItem({
																...session,
																copyForwardDisabled,
															});
														};
														const updateTestSession = (testSession) => {
															actions.setItem((prev) =>
																({ copyForwardDisabled: prev.copyForwardDisabled, ...testSession }));
														};
														return itemLoading ? <Loader />
															: error ? <h3>
																An error occurred while loading
																<br />
																<a onClick={() => actions.load()}>
																	try load again
																</a>
															</h3> : <>
																{/* <div className="row" style={{ paddingRight: '15px' }}>
																	{session
																		&& <QuestionnaireButtons
																			testSession={session}
																			signatureSrc={signatureSrc}
																			setSignatureSrc={setSignatureSrc}
																			setHasSign={setHasSign}
																			copyForwardDisabled={copyForwardDisabled}
																			setCopyForwardDisabled={setCopyForwardDisabled}
																			form={form}
																			updateTestSession={updateTestSession}
																			updateTestSessionAfterSign={false}
																		/>
																	}
																</div> */}
																<QuestionnaireForm
																	session={session}
																	setSession={updateTestSession}
																	afterSubmit={() => {
																		setAnsweredInvites((prev) => prev.concat(invite.id));
																		setNextInvite(invite);
																	}}
																	onCancel={setNextInvite}
																	queryParams={queryParams}
																	enableSave
																	hasSign={hasSigh
																		|| Boolean(signatureSrc)
																		|| Boolean(!session?.disease?.patientSign)}
																	setHasSign={setHasSign}
																	signatureSrc={signatureSrc}
																	setSignatureSrc={setSignatureSrc}
																	copyForwardDisabled={copyForwardDisabled}
																	nextBtnOnClick={setNextInvite}
																	allowSaveWithoutChanges
																/>
																{goToDashboard(setItems)}
															</>;
													}}
												</ItemProvider>
											);
										}
										return (
											<>
												<div className="row text-center mt10">
													<div className="col-sm-12">
														<h4>
															{invites[activeInviteIndex].name
																? invites[activeInviteIndex].name
																: null}
														</h4>
													</div>
												</div>
												<div className="row">
													<div className="col-sm-6">
														{invites[activeInviteIndex].templateSnapshot?.description
															? invites[activeInviteIndex].templateSnapshot?.description
															: null}
													</div>
												</div>
												<br />
												<ConsentFormSwitcher
													showPdf={false}
													queryParams={queryParams}
													onAccept={() => setNextInvite(invite)}
													onCancel={setNextInvite}
													patientFileId={invites[activeInviteIndex].patientFileId}
													enableSave
												/>
												{goToDashboard(setItems)}
											</>
										);
									}
									return null;
								})}
							</div>
						</div>
						: invites.length === 0
							? (
								<>
									<div>
										<div>
											All Forms confirmed
										</div>
									</div>
									{props.renderButtons && props.renderButtons(() => handleSubmit(answeredInvites, setItems))}
								</>
							)
							: <Loader />}
				</div>
			</>;
		}}
	</ItemsProviderWithStore>;
};
